import { toMobileDigits } from '@/lib/forms';
import type { InstituteFormValues } from './schemas/institute.schema';

/** The three numbers a new institute is given, and how each is named to the reader. */
const MOBILE_FIELDS = [
  ['phone', 'the school office'],
  ['principalPhone', 'the principal'],
  ['adminContactPhone', 'the admin'],
] as const;

/**
 * A note for when two or three of an institute's numbers are the same one.
 *
 * A warning, never an error: the server accepts it on purpose, because in a
 * small school the principal is often the administrator and the office phone
 * is their mobile too. Only the admin's number is a login, and the server
 * checks that against every other account when the institute is created. What
 * this catches is the number pasted into all three boxes by mistake.
 *
 * Null when every number is different, or not fully typed yet.
 */
export function sharedMobilesWarning(
  values: Pick<InstituteFormValues, 'phone' | 'principalPhone' | 'adminContactPhone'>,
): string | null {
  const holders = new Map<string, string[]>();
  for (const [field, who] of MOBILE_FIELDS) {
    const digits = toMobileDigits(values[field] ?? '');
    if (digits.length !== 10) continue;
    holders.set(digits, [...(holders.get(digits) ?? []), who]);
  }

  const shared = [...holders.values()].find((who) => who.length > 1);
  if (!shared) return null;

  const names =
    shared.length === 2 ? `${shared[0]} and ${shared[1]}` : `${shared.slice(0, -1).join(', ')} and ${shared.at(-1)}`;
  const subject = names.charAt(0).toUpperCase() + names.slice(1);

  return `${subject} ${shared.length === 2 ? 'have' : 'all have'} the same mobile number. That is fine if one person does both jobs — otherwise check it is not a typing mistake.`;
}
